/**
 * Client-side half of the closet admin credential. The server half lives in
 * `src/lib/closet/auth.ts` (`enforceClosetAuth`), which accepts the credential
 * *only* as an `Authorization: Bearer` header — so every write/generate call
 * from the browser must go through `closetAuthHeaders()`. Sending it under any
 * other header name silently 401s.
 *
 * The storage key lives here and nowhere else; read and write it through these
 * helpers rather than touching sessionStorage directly.
 */
const STORAGE_KEY = 'closet_auth_token'

/** Same-tab companion to the cross-tab `storage` event. */
export const CLOSET_AUTH_CHANGE_EVENT = 'closet-auth-change'

/** Reads the stored admin token. Returns null during SSR. */
export function readClosetAuthToken(): string | null {
  if (typeof window === 'undefined') return null
  return window.sessionStorage.getItem(STORAGE_KEY)
}

/**
 * Persists the admin token after a successful `/api/closet/auth` check and
 * notifies same-tab listeners. The native `storage` event only fires in *other*
 * tabs, so ClosetAuthContext would otherwise not see a login that happened in
 * its own tab until a reload.
 */
export function saveClosetAuthToken(token: string): void {
  if (typeof window === 'undefined') return
  window.sessionStorage.setItem(STORAGE_KEY, token)
  window.dispatchEvent(new Event(CLOSET_AUTH_CHANGE_EVENT))
}

/**
 * Merges the closet admin token into a Bearer `Authorization` header. Use this
 * for every client → closet write/generate call so the server-side
 * `enforceClosetAuth` guard receives the credential. Safe to call during SSR —
 * it returns the base headers unchanged when `window` is absent.
 */
export function closetAuthHeaders(
  base: Record<string, string> = {},
): Record<string, string> {
  const headers = { ...base }
  const token = readClosetAuthToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

/**
 * Exchanges a password for an authenticated session via `/api/closet/auth`,
 * storing the token on success. Returns whether the password was accepted;
 * throws only when the request itself fails (offline, DNS, etc.) so callers can
 * tell "wrong password" apart from "couldn't reach the server".
 */
export async function loginToCloset(password: string): Promise<boolean> {
  const response = await fetch('/api/closet/auth', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  })
  if (!response.ok) return false
  saveClosetAuthToken(password)
  return true
}
